// CalibrationLogsFilterMenu.js
// Focused filter panel for the Calibration Log Book list. Reuses the shared FilterMenu inputs and
// styles (modeled on DelFilterMenu) with calibration-relevant fields: Created Date range, Project,
// Phase, and Created By.

import React from "react";
import {
  ProjectSelectionInput,
  PhaseSelectionInput,
  SearchEmployeeInput,
} from "../../../common/FilterMenu";
import { DelDatePicker } from "../../../common/DelDatePicker";
import styles from "../../../common/FilterMenu.module.css";

export const CalibrationLogsFilterMenu = ({
  filters,
  updateFilterAction,
  onApplyFilters,
  onClearFilters,
  allProjectInfo,
  allInactiveProjects,
  allEmployees,
  filterPhaseList,
  filterPhaseActionFunction,
}) => {
  const handleFilterChange = (field, value) => {
    updateFilterAction({ [field]: value });
  };

  const handleSetSelectedProject = (project) => {
    if (!project) {
      handleFilterChange("projectNumber", "");
      handleFilterChange("projectLabel", "");
      filterPhaseActionFunction([]);
      // Clear dependent phase
      handleFilterChange("phase", "");
      handleFilterChange("phaseLabel", "");
      return;
    }
    filterPhaseActionFunction(project.phase_list);
    handleFilterChange("projectNumber", project.value);
    handleFilterChange("projectLabel", project.label);
    handleFilterChange("phase", "");
    handleFilterChange("phaseLabel", "");
  };

  const handleSetSelectedPhase = (phase) => {
    if (!phase) {
      handleFilterChange("phase", "");
      handleFilterChange("phaseLabel", "");
      return;
    }
    handleFilterChange("phase", phase.phase_id);
    handleFilterChange("phaseLabel", phase.label);
  };

  const handleSetSelectedCreatedBy = (employee) => {
    if (!employee) {
      handleFilterChange("createdBy", "");
      handleFilterChange("createdByName", "");
      return;
    }
    handleFilterChange("createdBy", employee.value);
    handleFilterChange("createdByName", employee.label);
  };

  const handleDatePickerDateChange = (field, date) => {
    if (!date) {
      handleFilterChange(field, "");
      return;
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    handleFilterChange(field, `${year}-${month}-${day}`);
  };

  return (
    <div className={styles.reportFilterMenu}>
      <div className={styles.headerContainer}>
        <h3>Filter Options</h3>
      </div>
      <div className={styles.filtersContainer}>
        {/* Created date range */}
        <div className={styles.filterRow}>
          <label>Created Date:</label>
          <div className={styles.datePickerContainer}>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>Start:</label>
              <DelDatePicker
                placeHolderText=""
                selected={filters?.createdStartDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("createdStartDate", date)
                }
                selectsStart
                startDate={filters?.createdStartDate || ""}
                endDate={filters?.createdEndDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
                maxDate={filters?.createdEndDate || ""}
              />
            </div>
            <div className={styles.datePickerGroup}>
              <label className={styles.dateLabel}>End:</label>
              <DelDatePicker
                placeHolderText=""
                selected={filters?.createdEndDate || ""}
                onChange={(date) =>
                  handleDatePickerDateChange("createdEndDate", date)
                }
                selectsEnd
                startDate={filters?.createdStartDate || ""}
                endDate={filters?.createdEndDate || ""}
                minDate={filters?.createdStartDate || ""}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className={styles.datePicker}
              />
            </div>
          </div>
        </div>

        {/* Project */}
        <div className={styles.filterRow}>
          <label htmlFor="project">Project:</label>
          <ProjectSelectionInput
            allProjectInfo={allProjectInfo || []}
            allInactiveProjects={allInactiveProjects || []}
            selectedProjectValue={filters?.projectNumber}
            selectedProjectLabel={filters?.projectLabel}
            selectedProjectPhaseList={filterPhaseList}
            setSelectedProjectFunction={handleSetSelectedProject}
          />
        </div>

        {/* Phase */}
        <div className={styles.filterRow}>
          <label htmlFor="phase">Phase:</label>
          <PhaseSelectionInput
            projectPhases={filterPhaseList}
            selectedPhaseValue={filters?.phase}
            selectedPhaseLabel={filters?.phaseLabel}
            setSelectedPhaseFunction={handleSetSelectedPhase}
          />
        </div>

        {/* Created By */}
        <div className={styles.filterRow}>
          <label htmlFor="createdBy">Created By:</label>
          <SearchEmployeeInput
            allEmployees={allEmployees || []}
            helpText="Search Employees"
            selectedEmployeeValue={filters?.createdBy}
            selectedEmployeeLabel={filters?.createdByName}
            setSelectedEmployeeFunction={handleSetSelectedCreatedBy}
          />
        </div>
      </div>
      <div className={styles.filterActions}>
        <button className={styles.clearButton} onClick={onClearFilters}>
          Clear
        </button>
        <button className={styles.applyButton} onClick={onApplyFilters}>
          Apply
        </button>
      </div>
    </div>
  );
};
